import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import calendarIcon from "assets/images/agendamento-fob-calendar.png";
import printerIcon from "assets/images/imprimir-icon-fob.png";
import kemiraLogo from "assets/images/logo-kemira-black-header.png";
import webcolLogo from "assets/images/logo-webcol-preta.png";
import previousArrow from "assets/images/prev-arrow.png";
import { api } from "services/api";
import { notify } from "services/notification";

import * as S from "../Schedule/styles";
import * as C from "./styles";

interface OrderData {
	id?: number;
	po_number?: string;
	company?: {
		trade_name?: string;
		address_street?: string;
		address_number?: string;
		address_city?: string;
		address_state?: string;
		address_zipcode?: string;
	};
}

interface SchedulingData {
	id?: number;
}

interface ConfirmationState {
	scheduling?: SchedulingData;
	order?: OrderData;
	deliveries?: OrderData[];
	dockName?: string;
	dockTime?: string;
}

interface ApiConfirmationResponse {
	id?: number;
	scheduling?: SchedulingData;
	order?: OrderData;
	deliveries?: OrderData[];
	orders?: OrderData[];
	dockName?: string;
	dockTime?: string;
	starts_at?: string;
	company?: OrderData["company"];
	dock?: { name?: string };
	date?: string;
	time?: string;
}

const formatAddress = (company?: OrderData["company"]) => {
	if (!company) return "--";
	return [
		company.address_street,
		company.address_number,
		company.address_city,
		company.address_state,
		company.address_zipcode,
	]
		.filter(Boolean)
		.join(", ");
};

const formatDateTime = (value?: string) => {
	if (!value) return "--";
	const [date, time] = value.replace("T", " ").split(" ");
	const [year, month, day] = (date || "").split("-");
	return `${day}/${month}/${year} ${time || ""}`.trim();
};

export const AgendamentoFobConfirmation: React.FC = () => {
	const history = useHistory();
	const { state } = useLocation<ConfirmationState>();
	const { schedulingId } = useParams<{ schedulingId?: string }>();
	const [searchedConfirmation, setSearchedConfirmation] =
		useState<ConfirmationState>();
	const [storedState] = useState<ConfirmationState | undefined>(() => {
		try {
			const stored = sessionStorage.getItem(
				"agendamento-fob-confirmation",
			);
			return stored
				? (JSON.parse(stored) as ConfirmationState)
				: undefined;
		} catch {
			return undefined;
		}
	});
	const storedStateMatchesUrl =
		!schedulingId ||
		storedState?.scheduling?.id?.toString() === schedulingId;
	const confirmation =
		state ||
		(storedStateMatchesUrl ? storedState : undefined) ||
		searchedConfirmation;
	const order = confirmation?.order;
	const deliveries = confirmation?.deliveries || (order ? [order] : []);

	const fetchConfirmation = async (id: string) => {
		const { data } = await api.get<ApiConfirmationResponse>(
			`/agendamento-fob/scheduling/${encodeURIComponent(id)}`,
		);
		const response = data || {};
		const responseOrders = response.deliveries || response.orders || [];
		const firstOrder = response.order || responseOrders[0];
		const nextConfirmation: ConfirmationState = {
			scheduling: response.scheduling || {
				id: response.id || Number(id),
			},
			order: firstOrder
				? { ...firstOrder, company: firstOrder.company || response.company }
				: { company: response.company },
			deliveries: responseOrders,
			dockName: response.dockName || response.dock?.name,
			dockTime:
				response.dockTime ||
				response.starts_at ||
				(response.date && response.time
					? `${response.date} ${response.time}`
					: undefined),
		};
		setSearchedConfirmation(nextConfirmation);
		return nextConfirmation;
	};

	useEffect(() => {
		if (!schedulingId || state || storedStateMatchesUrl) return;
		fetchConfirmation(schedulingId)
			.catch((error: any) => {
				const message =
					error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					"Não foi possível localizar o agendamento.";
				notify("error", message);
				});
		// A consulta deve ocorrer somente quando a URL for aberta diretamente.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schedulingId]);


	const handlePrint = () => window.print();
	const handleBack = () => {
		sessionStorage.removeItem("agendamento-fob-confirmation");
		history.push("/agendamento-fob");
	};

	if (!confirmation?.scheduling || !order) {
		return (
			<S.Page>
				<S.Container>
					<C.HeaderWrapper>
						<S.Header>
							<S.Logo src={kemiraLogo} alt="Kemira" />
							<S.HeaderActions>
								<S.BackButton
									type="button"
									onClick={handleBack}
								>
									<S.PreviousArrow
										src={previousArrow}
										alt=""
										aria-hidden="true"
									/>{" "}
									Voltar
								</S.BackButton>
								<S.AccessText>
									Acesso exclusivo para clientes Kemira
								</S.AccessText>
							</S.HeaderActions>
						</S.Header>
					</C.HeaderWrapper>
					<C.EmptyState>Nenhum agendamento foi selecionado.</C.EmptyState>
					<S.Footer>
						<span>Powered by GS Tecnologia e Inovação®</span>
						<img src={webcolLogo} alt="Webcol" />
					</S.Footer>
				</S.Container>
			</S.Page>
		);
	}

	return (
		<S.Page>
			<S.Container>
				<C.HeaderWrapper>
					<S.Header>
						<S.Logo src={kemiraLogo} alt="Kemira" />
						<S.HeaderActions>
							<S.BackButton type="button" onClick={handleBack}>
								<S.PreviousArrow
									src={previousArrow}
									alt=""
									aria-hidden="true"
								/>{" "}
								Voltar
							</S.BackButton>
							<S.AccessText>
								Acesso exclusivo para clientes Kemira
							</S.AccessText>
						</S.HeaderActions>
					</S.Header>
				</C.HeaderWrapper>

				<S.Content>
					<C.ConfirmationContent>
						<C.CalendarIcon
							src={calendarIcon}
							alt=""
							aria-hidden="true"
						/>
						<C.Title>Coleta agendada!</C.Title>
						<C.Details>
							<C.Detail>
								<strong>Número da coleta:</strong>
								<span>
									{schedulingId ||
										confirmation.scheduling.id ||
										"--"}
								</span>
							</C.Detail>
							<C.Detail>
								<strong>Sales Orders:</strong>
								<span>
									{deliveries
										.map((delivery) => delivery.po_number)
										.filter(Boolean)
										.join(", ") || "--"}
								</span>
							</C.Detail>
							<C.Detail>
								<strong>Doca:</strong>
								<span>{confirmation.dockName || "--"}</span>
							</C.Detail>
							<C.Detail>
								<strong>Data/hora:</strong>
								<span>
									{formatDateTime(confirmation.dockTime)}
								</span>
							</C.Detail>
							<C.Detail>
								<strong>Centro:</strong>
								<span>{order.company?.trade_name || "--"}</span>
							</C.Detail>
							<C.Detail>
								<strong>Endereço:</strong>
								<span>{formatAddress(order.company)}</span>
							</C.Detail>
						</C.Details>
						<C.PrintButton type="button" onClick={handlePrint}>
							<C.PrintIcon
								src={printerIcon}
								alt=""
								aria-hidden="true"
							/>{" "}
							Imprimir
						</C.PrintButton>
					</C.ConfirmationContent>
				</S.Content>

				<S.Footer>
					<span>Powered by GS Tecnologia e Inovação®</span>
					<img src={webcolLogo} alt="Webcol" />
				</S.Footer>
			</S.Container>
		</S.Page>
	);
};

export default AgendamentoFobConfirmation;
